// Document-to-document relationship map. The document itself sits in a card at
// the centre of the canvas; documents it cites ride two elliptical orbits -
// organisation documents on the left, external obligations on the right.
// Orbit position, node shape and line colour all carry meaning:
//   left  : green  = the cited document is in the organisation library
//           red    = cited but never uploaded, so the citation cannot resolve
//   right : amber  = regulation, violet = standard, teal = framework
// Line weight grows with the number of citations pointing at that document.
// Drawn in SVG (no layout loop), so it scales with the drawer and follows the
// app theme.
import type { CitationRow, CitedDoc } from "./utilities/types";
import {
  EXTERNAL_DOC_TYPES,
  GRAPH_ARC_SPREAD,
  GRAPH_EDGE_WEIGHT,
  GRAPH_EXTERNAL_FALLBACK,
  GRAPH_EXTERNAL_NODE,
  GRAPH_HUB,
  GRAPH_HUB_CENTER_Y,
  GRAPH_LEGEND_GROUPS,
  GRAPH_MAX_NODES_PER_SIDE,
  GRAPH_ORBIT,
  GRAPH_VIEWBOX,
  REL_COLOR,
  RELATIONSHIP_COPY,
  RELATIONSHIP_SECTIONS,
  UNNAMED_TARGET,
} from "./utilities/constants";
import { pluralSuffix, wrapLabel } from "./utilities/helpers";
import { RelCard } from "./parts";

interface GraphNode {
  key: string;
  name: string;
  caption: string;
  color: string;
  side: "left" | "right";
  dashed: boolean;
  weight: number;
  x: number;
  y: number;
}

const { width: W, height: H } = GRAPH_VIEWBOX;
const CX = W / 2;
const CY = GRAPH_HUB_CENTER_Y;
const HUB_HALF_W = GRAPH_HUB.width / 2;
const HUB_HALF_H = GRAPH_HUB.height / 2;
const RAD = Math.PI / 180;

function edgeWeightFor(count: number): number {
  return (
    GRAPH_EDGE_WEIGHT.base +
    Math.min(count, GRAPH_EDGE_WEIGHT.cap) * GRAPH_EDGE_WEIGHT.perCitation
  );
}

/** Place node i of n on the left or right arc of the orbit ellipse. */
function orbitPoint(i: number, n: number, side: "left" | "right") {
  const t = n <= 1 ? 0.5 : i / (n - 1);
  const angle = (side === "left" ? 180 : 0) + (side === "left" ? -1 : 1) * (-GRAPH_ARC_SPREAD + 2 * GRAPH_ARC_SPREAD * t);
  return {
    x: CX + GRAPH_ORBIT.rx * Math.cos(angle * RAD),
    y: CY + GRAPH_ORBIT.ry * Math.sin(angle * RAD),
  };
}

/** Arc of the orbit ellipse, drawn faintly behind the nodes. */
function orbitArc(side: "left" | "right"): string {
  const a = orbitPoint(0, 2, side);
  const b = orbitPoint(1, 2, side);
  // Left arc runs anti-clockwise on screen, right arc clockwise.
  const sweep = side === "left" ? 0 : 1;
  return `M${a.x},${a.y} A${GRAPH_ORBIT.rx},${GRAPH_ORBIT.ry} 0 0 ${sweep} ${b.x},${b.y}`;
}

function hexPoints(cx: number, cy: number, r: number): string {
  return [0, 1, 2, 3, 4, 5]
    .map((i) => {
      const a = (i * 60 - 90) * RAD;
      return `${(cx + r * Math.cos(a)).toFixed(1)},${(cy + r * Math.sin(a)).toFixed(1)}`;
    })
    .join(" ");
}

export function RelationshipGraphSection({
  docs,
  citations,
  documentTitle,
}: {
  docs: CitedDoc[];
  citations: CitationRow[];
  documentTitle?: string;
}) {
  // Left: only organisation documents this document actually cites. An unnamed
  // target ("Section 5") has no document to draw.
  const orgDocs = docs.filter((d) => d.name !== UNNAMED_TARGET && d.type === "INTERNAL_DOCUMENT");

  // Right: each distinct external standard, framework or regulation, with the
  // number of citations pointing at it.
  const extByLabel = new Map<string, { row: CitationRow; count: number }>();
  citations
    .filter((c) => EXTERNAL_DOC_TYPES.has(c.type))
    .forEach((c) => {
      const label = c.target !== UNNAMED_TARGET ? c.target : c.evidence;
      if (!label) return;
      const seen = extByLabel.get(label);
      if (seen) seen.count += 1;
      else extByLabel.set(label, { row: c, count: 1 });
    });
  const extDocs = Array.from(extByLabel.entries());

  const orgShown = orgDocs.slice(0, GRAPH_MAX_NODES_PER_SIDE);
  const extShown = extDocs.slice(0, GRAPH_MAX_NODES_PER_SIDE);
  const orgHidden = orgDocs.length - orgShown.length;
  const extHidden = extDocs.length - extShown.length;

  const left: GraphNode[] = orgShown.map((d, i) => {
    const count = d.evidence?.length ?? 0;
    return {
      key: `org-${d.key}`,
      name: d.name,
      caption: `${count} citation${pluralSuffix(count)} · ${d.resolved ? RELATIONSHIP_COPY.graphInLibrary : RELATIONSHIP_COPY.graphMissing}`,
      color: d.resolved ? REL_COLOR.green : REL_COLOR.red,
      side: "left",
      dashed: !d.resolved,
      weight: edgeWeightFor(count),
      ...orbitPoint(i, orgShown.length, "left"),
    };
  });

  const right: GraphNode[] = extShown.map(([label, { row, count }], i) => {
    const meta = GRAPH_EXTERNAL_NODE[row.type] ?? GRAPH_EXTERNAL_FALLBACK;
    return {
      key: `ext-${label}`,
      name: label,
      caption: `${count} citation${pluralSuffix(count)} · ${meta.label}`,
      color: meta.color,
      side: "right",
      dashed: false,
      weight: edgeWeightFor(count),
      ...orbitPoint(i, extShown.length, "right"),
    };
  });

  const hubLines = wrapLabel(documentTitle, 18, 2);
  const empty = left.length === 0 && right.length === 0;

  const renderEdge = (node: GraphNode, i: number) => {
    const dir = node.side === "left" ? -1 : 1;
    const sx = CX + dir * HUB_HALF_W;
    const sy = CY + Math.max(-HUB_HALF_H + 14, Math.min(HUB_HALF_H - 14, (node.y - CY) * 0.35));
    const ex = node.x - dir * 22;
    const dx = ex - sx;
    const d = `M${sx},${sy} C${sx + dx * 0.5},${sy} ${ex - dx * 0.45},${node.y} ${ex},${node.y}`;
    return (
      <g key={`edge-${node.key}`}>
        <path d={d} fill="none" stroke={node.color} strokeOpacity={0.07} strokeWidth={node.weight + 4} strokeLinecap="round" />
        <path
          d={d}
          fill="none"
          stroke={node.color}
          strokeOpacity={node.dashed ? 0.55 : 0.38}
          strokeWidth={node.weight}
          strokeLinecap="round"
          strokeDasharray={node.dashed ? "6 5" : undefined}
          className={node.dashed ? "rel-dash-flow" : undefined}
        />
        {!node.dashed && (
          <>
            {/* Soft halo behind the travelling segment, then the bright core. */}
            <path
              d={d}
              fill="none"
              stroke={node.color}
              strokeOpacity={0.3}
              strokeWidth={node.weight + 7}
              strokeLinecap="round"
              pathLength={100}
              strokeDasharray="16 84"
              className="rel-comet"
              style={{ animationDelay: `${(i * 0.6) % 4.5}s` }}
            />
            <path
              d={d}
              fill="none"
              stroke={node.color}
              strokeOpacity={0.95}
              strokeWidth={node.weight + 1.4}
              strokeLinecap="round"
              pathLength={100}
              strokeDasharray="14 86"
              className="rel-comet"
              style={{ animationDelay: `${(i * 0.6) % 4.5}s` }}
            />
          </>
        )}
      </g>
    );
  };

  const renderNode = (node: GraphNode) => {
    const dir = node.side === "left" ? -1 : 1;
    const labelX = node.x + dir * 24;
    const anchor = node.side === "left" ? "end" : "start";
    const lines = wrapLabel(node.name, 20, 2);
    const top = node.y - (lines.length - 1) * 6.5 - 3;
    return (
      <g key={node.key}>
        <title>{`${node.name}: ${node.caption}`}</title>
        <circle cx={node.x} cy={node.y} r={21} fill={node.color} fillOpacity={0.05} />
        {node.side === "right" ? (
          <polygon points={hexPoints(node.x, node.y, 13)} className="fill-card" stroke={node.color} strokeWidth={1.5} strokeOpacity={0.7} />
        ) : (
          <circle cx={node.x} cy={node.y} r={12} className="fill-card" stroke={node.color} strokeWidth={1.5} strokeOpacity={0.7} />
        )}
        <circle cx={node.x} cy={node.y} r={3.5} fill={node.color} fillOpacity={0.75} />
        {lines.map((line, i) => (
          <text
            key={`${node.key}-l${i}`}
            x={labelX}
            y={top + i * 13}
            textAnchor={anchor}
            fontSize={11.5}
            fontWeight={700}
            className="fill-foreground"
          >
            {line}
          </text>
        ))}
        <text x={labelX} y={top + lines.length * 13 + 1} textAnchor={anchor} fontSize={9.5} fill={node.color} fillOpacity={0.85}>
          {node.caption}
        </text>
      </g>
    );
  };

  const zoneLabel = (side: "left" | "right", text: string, count: number, color: string) => {
    const x = side === "left" ? 24 : W - 24;
    return (
      <g>
        <circle cx={side === "left" ? x + 5 : x - 5} cy={22} r={3.5} fill={color} fillOpacity={0.7} />
        <text
          x={side === "left" ? x + 16 : x - 16}
          y={26}
          textAnchor={side === "left" ? "start" : "end"}
          fontSize={11}
          fontWeight={700}
          letterSpacing={0.4}
          className="fill-foreground"
        >
          {`${text} · ${count}`}
        </text>
      </g>
    );
  };

  return (
    <RelCard
      title={RELATIONSHIP_SECTIONS.graph.title}
      dot={RELATIONSHIP_SECTIONS.graph.dot}
      right={RELATIONSHIP_SECTIONS.graph.right}
      flush
    >
      <div className="relative overflow-hidden bg-gradient-to-b from-muted/20 to-background">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="block h-auto w-full min-h-[320px]"
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label={`Relationship map: ${orgDocs.length} organisation documents cited, ${extDocs.length} external documents cited`}
        >
          <defs>
            <radialGradient id="rel-graph-glow">
              <stop offset="0%" stopColor={REL_COLOR.accent} stopOpacity={0.1} />
              <stop offset="100%" stopColor={REL_COLOR.accent} stopOpacity={0} />
            </radialGradient>
            <linearGradient id="rel-hub-fill" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor={REL_COLOR.accent} stopOpacity={0.09} />
              <stop offset="100%" stopColor={REL_COLOR.cyan} stopOpacity={0.04} />
            </linearGradient>
          </defs>

          {/* Orbits */}
          <circle cx={CX} cy={CY} r={150} fill="url(#rel-graph-glow)" />
          <ellipse
            cx={CX}
            cy={CY}
            rx={GRAPH_ORBIT.rx}
            ry={GRAPH_ORBIT.ry}
            fill="none"
            className="stroke-border"
            strokeDasharray="3 7"
            strokeOpacity={0.4}
          />
          <ellipse
            cx={CX}
            cy={CY}
            rx={GRAPH_ORBIT.rx * 0.62}
            ry={GRAPH_ORBIT.ry * 0.62}
            fill="none"
            className="stroke-border"
            strokeDasharray="3 9"
            strokeOpacity={0.22}
          />
          <path d={orbitArc("left")} fill="none" stroke={REL_COLOR.green} strokeOpacity={0.1} strokeWidth={2} />
          <path d={orbitArc("right")} fill="none" stroke={REL_COLOR.purple} strokeOpacity={0.1} strokeWidth={2} />

          {zoneLabel("left", RELATIONSHIP_COPY.graphOrgZone, orgDocs.length, REL_COLOR.green)}
          {zoneLabel("right", RELATIONSHIP_COPY.graphExtZone, extDocs.length, REL_COLOR.purple)}

          {left.map((n, i) => renderEdge(n, i))}
          {right.map((n, i) => renderEdge(n, i + left.length))}

          {/* Hub: this document */}
          <rect
            x={CX - HUB_HALF_W}
            y={CY - HUB_HALF_H}
            width={GRAPH_HUB.width}
            height={GRAPH_HUB.height}
            rx={18}
            fill="url(#rel-hub-fill)"
            stroke={REL_COLOR.accent}
            strokeOpacity={0.55}
            strokeWidth={1.5}
          />
          <text
            x={CX}
            y={CY - HUB_HALF_H + 22}
            textAnchor="middle"
            fontSize={9.5}
            fontWeight={700}
            letterSpacing={0.6}
            fill={REL_COLOR.accent}
            fillOpacity={0.6}
          >
            {RELATIONSHIP_COPY.graphHubCaption}
          </text>
          {hubLines.map((line, i) => (
            <text
              key={`hub-${i}`}
              x={CX}
              y={CY + 6 + (i - (hubLines.length - 1) / 2) * 16}
              textAnchor="middle"
              fontSize={13}
              fontWeight={800}
              className="fill-foreground"
            >
              {line}
            </text>
          ))}
          <text x={CX} y={CY + HUB_HALF_H - 12} textAnchor="middle" fontSize={9.5} className="fill-muted-foreground">
            {`${orgDocs.length + extDocs.length} documents cited`}
          </text>

          {left.map(renderNode)}
          {right.map(renderNode)}

          {orgHidden > 0 && (
            <text x={24} y={H - 60} fontSize={10} className="fill-muted-foreground">
              {`+${orgHidden} ${RELATIONSHIP_COPY.graphMoreOrg}`}
            </text>
          )}
          {extHidden > 0 && (
            <text x={W - 24} y={H - 60} textAnchor="end" fontSize={10} className="fill-muted-foreground">
              {`+${extHidden} ${RELATIONSHIP_COPY.graphMoreExt}`}
            </text>
          )}

          {empty && (
            <text x={CX} y={CY + 130} textAnchor="middle" fontSize={12} className="fill-muted-foreground">
              {RELATIONSHIP_COPY.graphEmpty}
            </text>
          )}
          <text x={CX} y={H - 62} textAnchor="middle" fontSize={9} className="fill-muted-foreground">
            {RELATIONSHIP_COPY.graphFooter}
          </text>
        </svg>

        {/* Legend */}
        <div className="flex justify-center px-3 pb-3 sm:absolute sm:bottom-5 sm:left-1/2 sm:-translate-x-1/2 sm:p-0">
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 rounded-[20px] border border-border bg-card px-4 py-2 text-[10px]">
            {GRAPH_LEGEND_GROUPS.map((group) => (
              <span key={group.title} className="inline-flex flex-wrap items-center gap-x-2.5 gap-y-1">
                <span className="text-[9px] font-bold tracking-[0.6px] text-muted-foreground">{group.title}</span>
                {group.items.map((item) => (
                  <span key={item.label} className="inline-flex items-center gap-1 font-medium text-foreground">
                    <span className="size-[7px] rounded-full" style={{ background: item.color, opacity: 0.8 }} />
                    {item.label}
                  </span>
                ))}
              </span>
            ))}
          </div>
        </div>
      </div>
    </RelCard>
  );
}
